import React, { useState } from 'react';
import { Player, GameState } from '../../domain/entities';
import { getPlayerValue } from '../../domain/constants';
import { Modal } from '../shared/Modal';

interface AdjustListingPriceModalProps {
    player: Player | null;
    onClose: () => void;
    onConfirm: (newPrice: number) => void;
    gameState: GameState;
    leagueName: string;
}

export const AdjustListingPriceModal: React.FC<AdjustListingPriceModalProps> = ({ player, onClose, onConfirm, gameState, leagueName }) => {
    const [customPrice, setCustomPrice] = useState<string>('');

    if (!player) return null;

    const baseValue = getPlayerValue(player, leagueName, gameState.gameType, gameState.config);
    const currentPrice = player.askingPrice || baseValue;
    const inputPrice = customPrice ? parseInt(customPrice) : null;
    const priceChange = inputPrice ? inputPrice - currentPrice : 0;

    const handleConfirm = () => {
        if (!inputPrice || inputPrice <= 0) {
            alert('Please enter a valid price greater than 0');
            return;
        }
        onConfirm(inputPrice);
    };

    return (
        <Modal isOpen={!!player} onClose={onClose}>
            <h2>Adjust Listing Price</h2>
            <p>Player: <strong>{player.name}</strong></p>
            <p>Base market value: £{baseValue.toLocaleString()}</p>
            <p>Current asking price: <strong>£{currentPrice.toLocaleString()}</strong></p>

            <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                <p style={{ marginBottom: '8px' }}>Quick Adjustments:</p>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                        className={`button ${customPrice === String(Math.round(currentPrice * 0.9)) ? 'secondary' : ''}`}
                        onClick={() => setCustomPrice(String(Math.round(currentPrice * 0.9)))}
                        style={{ flex: '1', minWidth: '80px' }}
                    >
                        -10%
                        <br />
                        <span style={{ fontSize: '0.8em', opacity: 0.8 }}>£{Math.round(currentPrice * 0.9).toLocaleString()}</span>
                    </button>
                    <button
                        className={`button ${customPrice === String(currentPrice) ? 'secondary' : ''}`}
                        onClick={() => setCustomPrice(String(currentPrice))}
                        style={{ flex: '1', minWidth: '80px' }}
                    >
                        Current
                        <br />
                        <span style={{ fontSize: '0.8em', opacity: 0.8 }}>£{currentPrice.toLocaleString()}</span>
                    </button>
                    <button
                        className={`button ${customPrice === String(Math.round(currentPrice * 1.1)) ? 'secondary' : ''}`}
                        onClick={() => setCustomPrice(String(Math.round(currentPrice * 1.1)))}
                        style={{ flex: '1', minWidth: '80px' }}
                    >
                        +10%
                        <br />
                        <span style={{ fontSize: '0.8em', opacity: 0.8 }}>£{Math.round(currentPrice * 1.1).toLocaleString()}</span>
                    </button>
                </div>
            </div>

            <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px' }}>
                    <strong>Custom Price:</strong>
                </label>
                <input
                    type="number"
                    value={customPrice}
                    onChange={(e) => setCustomPrice(e.target.value)}
                    placeholder={`Enter custom price (e.g., ${currentPrice.toLocaleString()})`}
                    style={{
                        width: '100%',
                        padding: '8px 12px',
                        fontSize: '1em',
                        backgroundColor: 'var(--primary-color)',
                        color: 'var(--text-color)',
                        border: '1px solid var(--highlight-color)',
                        borderRadius: '4px',
                        boxSizing: 'border-box'
                    }}
                />
            </div>

            {inputPrice && (
                <p style={{ textAlign: 'center', fontWeight: 'bold' }}>
                    New asking price: £{inputPrice.toLocaleString()}
                    {priceChange !== 0 && (
                        <span style={{ color: priceChange > 0 ? 'var(--positive-color)' : 'var(--warning-color)', marginLeft: '8px' }}>
                            ({priceChange > 0 ? '+' : ''}{priceChange.toLocaleString()})
                        </span>
                    )}
                </p>
            )}

            <p style={{ fontSize: '0.9em', opacity: 0.8, marginTop: '12px' }}>
                Lower prices increase chances of a quick sale. Higher prices may take longer to sell or not sell at all.
            </p>

            <div className="modal-actions">
                <button className="button" onClick={onClose}>Cancel</button>
                <button className="button primary" onClick={handleConfirm} disabled={!inputPrice || inputPrice <= 0}>Confirm</button>
            </div>
        </Modal>
    );
};
